import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';

import { Environment } from '@libs/types';
import { getJurisdictionBaseUrl } from '@libs/helpers';
import { FeatureModuleEnum, JurisdictionsEnum } from '@libs/backend-common';

import { PublicRoute, PrivateRoute, FeatureRenderer } from 'src/components';
import { APP_LINKS, AUTHENTICATOR_LINK } from 'src/constants';

import LoginPage from './login/LoginPage';
import Dashboard from './dashboard/Dashboard';
import TwoFaPage from './two-factor/TwoFaPage';
import RegisterPage from './register/RegisterPage';
import PageNotFound from './page-not-found/PageNotFound';

import VerifyEmailPage from './verify-email/VerifyEmailPage';
import ResetPasswordPage from './reset-password/ResetPasswordPage';
import PasswordRecoveryPage from './password-recovery/PasswordRecoveryPage';
import * as LazyLoaded from './lazy-loader';
import SwapTaskDetailsPage from './swap-task-details/SwapTaskDetailsPage';
import SwapDetailsPage from './swap-details/SwapDetailsPage';
import AssetDetailsPage from './asset-details/AssetDetailsPage';
import ActivateTwoFaPage from './activate-two-fa-page/ActivateTwoFaPage';
import { OSNavigator } from './operating-system-navigator/OperatingSystemNavigaor';
import CoinDetailsPage from './coin-details/CoinDetailsPage';
import TestPage from './test/Test';

const { jurisdictionId } = getJurisdictionBaseUrl();
const isJurisdictionGe = jurisdictionId === JurisdictionsEnum.GE;

const convertFeature = [FeatureModuleEnum.EXCHANGE];
const depositFeatures = [
  FeatureModuleEnum.BANK_WIRE_DEPOSIT,
  FeatureModuleEnum.CARD_DEPOSIT,
  FeatureModuleEnum.CASH_DEPOSIT,
  FeatureModuleEnum.CRYPTO_DEPOSIT,
];

const withdrawFeatures = [
  FeatureModuleEnum.BANK_WIRE_WITHDRAWAL,
  FeatureModuleEnum.CARD_WITHDRAWAL,
  FeatureModuleEnum.CASH_WITHDRAWAL,
  FeatureModuleEnum.CRYPTO_WITHDRAWAL,
];

const walletAndTransactionsFeatures = [
  ...depositFeatures,
  ...withdrawFeatures,
  ...convertFeature,
];

const getPrivateRoutesChildren = () => {
  const children = [
    { index: true, element: <LazyLoaded.DashboardContent /> },
    { path: 'feedback', element: <LazyLoaded.FeedbackPage /> },

    { path: 'kyc', element: <LazyLoaded.KycPage /> },

    ...(isJurisdictionGe
      ? [{ path: 'referrals', element: <LazyLoaded.ReferralsPage /> }]
      : []),
    ...(isJurisdictionGe
      ? [{ path: 'actions', element: <LazyLoaded.ActionsPage /> }]
      : []),
    {
      path: 'wallet',
      element: (
        <FeatureRenderer
          acceptedFeature={walletAndTransactionsFeatures}
          shouldCheckKyc={false}
          shouldNavigate
        >
          {jurisdictionId === JurisdictionsEnum.GE ? (
            <LazyLoaded.WalletPageGe />
          ) : (
            <LazyLoaded.WalletPage />
          )}
        </FeatureRenderer>
      ),
    },
    ...(jurisdictionId !== JurisdictionsEnum.GE
      ? [
          {
            path: 'asset',
            element: <AssetDetailsPage />,
          },
        ]
      : [
          {
            path: 'asset',
            element: <CoinDetailsPage />,
          },
        ]),

    ...(jurisdictionId !== JurisdictionsEnum.GE
      ? [
          {
            path: 'transactions',
            element: (
              <FeatureRenderer
                acceptedFeature={walletAndTransactionsFeatures}
                shouldCheckKyc={false}
                shouldNavigate
              >
                <LazyLoaded.TransactionsPage />
              </FeatureRenderer>
            ),
          },
        ]
      : []),
    ...(jurisdictionId === JurisdictionsEnum.GE
      ? [
          {
            path: 'transactions',
            element: (
              <FeatureRenderer
                acceptedFeature={walletAndTransactionsFeatures}
                shouldCheckKyc={false}
                shouldNavigate
              >
                <LazyLoaded.TransactionsPageGe />
              </FeatureRenderer>
            ),
          },
        ]
      : []),

    ...(jurisdictionId === JurisdictionsEnum.GE
      ? [
          {
            path: 'loyalty',
            element: <LazyLoaded.LoyaltyPage />,
          },
          {
            path: 'buy-crypto-loading',
            element: <LazyLoaded.PaymentLoadingPage />,
          },
        ]
      : []),
    ...(jurisdictionId === JurisdictionsEnum.GE
      ? [
          {
            path: 'dashboard-fees',
            element: <LazyLoaded.DashboardFeesPage />,
          },
          // {
          //   path: 'support-info',
          //   element: <Navigate to="/support-info-page/general" replace />,
          // },
          // {
          //   path: 'support-info/:infoPath',
          //   element: <LazyLoaded.SupportInfoPage />,
          // },
        ]
      : []),
    {
      path: 'account-information',
      element: <Navigate to="/account-information/personal" replace />,
    },
    {
      path: 'more-pages',
      element: <LazyLoaded.MobileMorePage />,
    },
    {
      path: 'account-information/:infoPath',
      element: <LazyLoaded.AccountInformationPage />,
    },
    {
      path: 'deposit',
      element: (
        <FeatureRenderer
          acceptedFeature={depositFeatures}
          shouldNavigate
          shouldCheckKyc={false}
        >
          <LazyLoaded.DepositPage />
        </FeatureRenderer>
      ),
    },
    {
      path: 'withdraw',
      element: (
        <FeatureRenderer
          acceptedFeature={withdrawFeatures}
          shouldNavigate
          shouldCheckKyc={false}
        >
          <LazyLoaded.WithdrawalPage />
        </FeatureRenderer>
      ),
    },
    ...(jurisdictionId === JurisdictionsEnum.UA ||
    process.env.ENVIRONMENT === Environment.dev
      ? [
          {
            path: 'swap-details',
            element: <SwapDetailsPage />,
          },
          {
            path: 'swap-task-details',
            element: <SwapTaskDetailsPage />,
          },
        ]
      : []),

    {
      path: 'convert',
      element: <Navigate to="BUY" replace />,
    },
    {
      path: 'convert/:type',
      element: (
        <FeatureRenderer
          acceptedFeature={convertFeature}
          shouldNavigate
          shouldCheckKyc={false}
        >
          <LazyLoaded.TradePage />
        </FeatureRenderer>
      ),
    },
    {
      path: 'sell/:currency?',
      element: (
        <FeatureRenderer
          acceptedFeature={convertFeature}
          shouldNavigate
          shouldCheckKyc={false}
        >
          <LazyLoaded.SellCrypto />
        </FeatureRenderer>
      ),
    },
    ...(jurisdictionId === JurisdictionsEnum.EU
      ? [
          {
            path: 'ticket',
            element: <LazyLoaded.TicketPage />,
          },
        ]
      : []),
    {
      path: '/not-found',
      element: <PageNotFound />,
    },
    {
      path: '*',
      element: <Navigate to="/not-found" replace />,
    },
  ];

  return children;
};

const router = createBrowserRouter([
  // Dashboard
  {
    path: '/',
    element: <PrivateRoute component={<Dashboard />} />,
    children: getPrivateRoutesChildren(),
  },
  {
    path: 'activate-2FA',
    element: <PrivateRoute component={<ActivateTwoFaPage />} />,
  },
  {
    path: 'verify-email',
    element: <VerifyEmailPage />,
  },
  // { path: 'components', element: <CommonComponents /> },
  // { path: 'two-factor-auth/google', element: <CommonComponents /> },
  { path: 'login', element: <PublicRoute component={<LoginPage />} /> },
  { path: 'test', element: <PublicRoute component={<TestPage />} /> },

  {
    path: 'register',
    element: <PublicRoute component={<RegisterPage />} />,
  },

  {
    path: 'password-recovery',
    element: <PublicRoute component={<PasswordRecoveryPage />} />,
  },
  {
    path: 'authenticator-link',
    element: (
      <PublicRoute
        component={
          <OSNavigator
            androidLink={AUTHENTICATOR_LINK.googlePlay}
            iosLink={AUTHENTICATOR_LINK.appStore}
          />
        }
      />
    ),
  },

  {
    path: 'app-links',
    element: (
      <PublicRoute
        component={
          <OSNavigator
            androidLink={APP_LINKS.googlePlay}
            iosLink={APP_LINKS.appStore}
          />
        }
      />
    ),
  },

  //We don't use it anymore
  {
    path: 'password-reset/:resetToken',
    element: <PublicRoute component={<ResetPasswordPage />} />,
  },
  {
    path: 'two-factor-auth',
    element: <PublicRoute component={<TwoFaPage />} />,
  },
]);

const Router = () => {
  return <RouterProvider router={router} />;
};

export default Router;
